// design/AccountSettings.dc.html + design/Usage.dc.html, folded into one page — FR-AD1 lists
// "storage and usage dashboard" as part of the org admin console, not a separate route. Split
// into tabs per the 2026-10 "Org Settings — tabbed layout" handoff; cards and copy unchanged.
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { Badge, Card, Icon, IconButton, Select, Tabs, ToggleRow } from "@grackle/ui";
import { SettingsSection, SaveRow } from "./SettingsSection";
import { api, ApiFail } from "../api/client";
import type {
  AccessTokenCreated,
  AccessTokenOut,
  GrackleVisibility,
  JoinLinkCreated,
  JoinLinkOut,
  JoinableRole,
  ListResponse,
  OrgDomainOut,
  OrgExportOut,
  OrgOut,
  MeetingConsentMode,
  MeetingSeriesShareOut,
  OrgPolicyOut,
  OrgUsageOut,
} from "../api/types";
import { formatBytes } from "../library/format";
import { useSession } from "../session";
import { Button, Field, Notice } from "../components/ui";
import { Stat } from "../components/Stat";
import { TokenAccessPicker, accessToScopes, scopesLabel, type TokenAccess } from "../components/TokenScopes";
import type { AdminOutletContext } from "./AdminLayout";

function Toggle({ on, onChange, label, sub }: { on: boolean; onChange: (v: boolean) => void; label: string; sub: string }) {
  return <ToggleRow label={label} description={sub} checked={on} onChange={onChange} divided />;
}

// Org policy settings (default visibility, public links, and the download toggle — stored now,
// enforced once downloads exist; see 0008_org_policy.sql's doc comment). FR-EN7: the comments
// toggle went with comments themselves.
function PolicySection() {
  const { org } = useOutletContext<AdminOutletContext>();
  const [policy, setPolicy] = useState<OrgPolicyOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    api<OrgPolicyOut>("GET", `/orgs/${org.id}/policy`)
      .then(setPolicy)
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load policy."));
  }, [org.id]);

  useEffect(() => {
    load();
  }, [load]);

  async function save(next: OrgPolicyOut) {
    setPolicy(next);
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await api<OrgPolicyOut>("PATCH", `/orgs/${org.id}/policy`, {
        default_visibility: next.default_visibility,
        allow_public_links: next.allow_public_links,
        allow_downloads: next.allow_downloads,
      });
      setPolicy(res);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't save that.");
    } finally {
      setSaving(false);
    }
  }

  if (!policy) return error ? <Notice tone="error">{error}</Notice> : <div className="admin-row-sub">Loading…</div>;

  return (
    <SettingsSection title="Policy" description="What new recordings default to, and what members may share.">
      <Select
        id="default-visibility"
        className="admin-field--narrow"
        label="Default visibility for new recordings"
        value={policy.default_visibility}
        onChange={(v) => save({ ...policy, default_visibility: v as GrackleVisibility })}
        options={[
          { value: "private", label: "Private" },
          { value: "specific", label: "Specific people" },
          { value: "team", label: "Team" },
          { value: "org", label: `Everyone at ${org.name}` },
          { value: "public", label: "Public" },
        ]}
      />
      <div className="admin-toggles admin-toggles--ruled">
      <Toggle
        on={policy.allow_public_links}
        onChange={(v) => save({ ...policy, allow_public_links: v })}
        label="Allow public share links"
        sub="Members can create links anyone with the URL can open."
      />
      <Toggle
        on={policy.allow_downloads}
        onChange={(v) => save({ ...policy, allow_downloads: v })}
        label="Allow downloads"
        sub="Takes effect once a download action ships — the setting is saved now either way."
      />
      </div>
      {error ? <Notice tone="error">{error}</Notice> : null}
      {saving ? <div className="admin-row-sub">Saving…</div> : saved ? <div className="admin-row-sub">Saved.</div> : null}
    </SettingsSection>
  );
}

export function MeetingPolicySection() {
  const { org } = useOutletContext<AdminOutletContext>();
  const [policy, setPolicy] = useState<OrgPolicyOut | null>(null);
  const [series, setSeries] = useState<MeetingSeriesShareOut[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [seriesError, setSeriesError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [seriesKey, setSeriesKey] = useState("");
  // Per-series destinations (FR-M10a: every one is opt-in, none on by default except the
  // email the form has always created).
  const [seriesDest, setSeriesDest] = useState({ email: true, slack: false, teams: false, cipher: false, calendar: false });

  const load = useCallback(() => {
    api<OrgPolicyOut>("GET", `/orgs/${org.id}/policy`)
      .then(setPolicy)
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load meeting policy."));
    api<ListResponse<MeetingSeriesShareOut>>("GET", `/orgs/${org.id}/meeting-series-share`)
      .then((res) => setSeries(res.items))
      .catch(() => setSeries([]));
  }, [org.id]);

  useEffect(() => {
    load();
  }, [load]);

  async function save(patch: Partial<OrgPolicyOut>) {
    if (!policy) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await api<OrgPolicyOut>("PATCH", `/orgs/${org.id}/policy`, {
        default_visibility: policy.default_visibility,
        allow_public_links: policy.allow_public_links,
        allow_downloads: policy.allow_downloads,
        meeting_consent_mode: patch.meeting_consent_mode ?? policy.meeting_consent_mode,
        meeting_require_consent_prompt: patch.meeting_require_consent_prompt ?? policy.meeting_require_consent_prompt,
        meeting_org_participants_only: patch.meeting_org_participants_only ?? policy.meeting_org_participants_only,
        meeting_notify_email_attendees: patch.meeting_notify_email_attendees ?? policy.meeting_notify_email_attendees,
        notetaker_bot_enabled: patch.notetaker_bot_enabled ?? policy.notetaker_bot_enabled,
        allow_desktop_meeting_capture: patch.allow_desktop_meeting_capture ?? policy.allow_desktop_meeting_capture,
      });
      setPolicy(res);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't save meeting policy.");
    } finally {
      setSaving(false);
    }
  }

  async function addSeries() {
    const key = seriesKey.trim();
    if (!key) return;
    setSeriesError(null);
    try {
      await api("POST", `/orgs/${org.id}/meeting-series-share`, {
        series_key: key,
        email_attendees: seriesDest.email,
        slack: seriesDest.slack,
        calendar: seriesDest.calendar,
        teams: seriesDest.teams,
        cipher: seriesDest.cipher,
      });
      setSeriesKey("");
      load();
    } catch (err) {
      setSeriesError(err instanceof ApiFail ? err.message : "Couldn't save that series.");
    }
  }

  if (!policy) return error ? <Notice tone="error">{error}</Notice> : <div className="admin-row-sub">Loading…</div>;

  return (
    <>
      {/* FR-M4: consent defaults to all-party and can only be relaxed. FR-M10: post-meeting share is
          never on by default. FR-ST6: ready notices link to the watch page, never a signed media URL. */}
      <SettingsSection
        title="Consent and post-meeting share"
        description="Grackle assumes everyone in a meeting must be told it's being recorded, and you can only relax that. Attendees are never sent a recording unless you turn it on, and the email links to the recording's page, not the video file."
      >
        <Select
          id="consent-mode"
          className="admin-field--narrow"
          label="Consent mode"
          value={policy.meeting_consent_mode}
          onChange={(v) => save({ meeting_consent_mode: v as MeetingConsentMode })}
          options={[
            { value: "all_party", label: "All-party (default)" },
            { value: "one_party", label: "One-party (relaxed)" },
          ]}
        />
        <div className="admin-toggles admin-toggles--ruled">
        <Toggle
          on={policy.meeting_require_consent_prompt}
          onChange={(v) => save({ meeting_require_consent_prompt: v })}
          label="Require consent prompt before capture"
          sub="Blocks local capture (extension and live meeting) until the capturing user acknowledges."
        />
        <Toggle
          on={policy.meeting_org_participants_only}
          onChange={(v) => save({ meeting_org_participants_only: v })}
          label="Org participants only"
          sub="Reject a participant list that includes an email outside this organization."
        />
        <Toggle
          on={policy.meeting_notify_email_attendees}
          onChange={(v) => save({ meeting_notify_email_attendees: v })}
          label="Email attendees when the grackle is ready"
          sub="Off until you turn it on. Sent when the recording is ready; if an email fails, the recording is unaffected."
        />
        <Toggle
          on={policy.notetaker_bot_enabled}
          onChange={(v) => save({ notetaker_bot_enabled: v })}
          label="Allow the notetaker bot"
          // FR-M3c: a visibly labelled, camera- and mic-off guest.
          sub="Joins Meet, Zoom and Teams as a clearly labelled guest with its camera and microphone off. Off stops both sending it to a meeting and automatic recording."
        />
        <Toggle
          on={policy.allow_desktop_meeting_capture}
          onChange={(v) => save({ allow_desktop_meeting_capture: v })}
          label="Allow desktop meeting capture"
          // FR-RD15: the capturing user always sees the recording HUD.
          sub="Lets the desktop app record a detected Zoom or Teams window as a meeting. On by default; the person recording always sees the recording bar."
        />
        </div>
        {error ? <Notice tone="error">{error}</Notice> : null}
        {saving ? <div className="admin-row-sub">Saving…</div> : saved ? <div className="admin-row-sub">Saved.</div> : null}
      </SettingsSection>

      <SettingsSection
        title="Per-series opt-in"
        description={series.length === 0 ? "No series overrides. Org toggles above apply to every meeting." : undefined}
      >
        {series.length > 0 ? (
          <div className="list">
            {series.map((s) => (
              <div className="list-row" key={s.id}>
                <div className="list-row__body">
                  <div className="list-row__title">
                    {s.series_key}
                    {s.email_attendees ? <Badge size="sm">email</Badge> : null}
                    {s.slack ? <Badge size="sm">slack</Badge> : null}
                    {s.teams ? <Badge size="sm">teams</Badge> : null}
                    {s.cipher ? <Badge size="sm">cipher</Badge> : null}
                    {s.calendar ? <Badge size="sm">calendar</Badge> : null}
                  </div>
                </div>
                <IconButton icon="trash" label={`Remove ${s.series_key}`} size="sm" variant="muted" onClick={() => {
                    void api("DELETE", `/orgs/${org.id}/meeting-series-share/${s.id}`).then(load).catch((err) => {
                      setSeriesError(err instanceof ApiFail ? err.message : "Couldn't remove that series.");
                    });
                  }} />
              </div>
            ))}
          </div>
        ) : null}
        <Card tone="sunken" className="org-settings__inset">
          <Field label="Meet code" className="admin-field--narrow" placeholder="abc-defg-hij" value={seriesKey} onChange={(e) => setSeriesKey(e.target.value)} />
          <div className="admin-toggles">
          <Toggle
            on={seriesDest.email}
            onChange={(v) => setSeriesDest({ ...seriesDest, email: v })}
            label="Email attendees"
            sub="For this series only."
          />
          <Toggle
            on={seriesDest.cipher}
            onChange={(v) => setSeriesDest({ ...seriesDest, cipher: v })}
            label="Post to Cipher"
            sub="Uses the Cipher channel connected under Integrations."
          />
          <Toggle
            on={seriesDest.slack}
            onChange={(v) => setSeriesDest({ ...seriesDest, slack: v })}
            label="Post to Slack"
            sub="Uses the Slack webhook under Integrations."
          />
          <Toggle
            on={seriesDest.teams}
            onChange={(v) => setSeriesDest({ ...seriesDest, teams: v })}
            label="Post to Teams"
            sub="Uses the Teams webhook under Integrations."
          />
          <Toggle
            on={seriesDest.calendar}
            onChange={(v) => setSeriesDest({ ...seriesDest, calendar: v })}
            label="Add notes to the calendar event"
            sub="Needs the recording owner's calendar write consent (Meetings → Calendar)."
          />
          </div>
          <div>
            <Button variant="secondary" onClick={() => void addSeries()} disabled={!seriesKey.trim()}>
              Save series
            </Button>
          </div>
        </Card>
        {seriesError ? <Notice tone="error">{seriesError}</Notice> : null}
      </SettingsSection>
    </>
  );
}

// Domain verification + auto-join.
function DomainsSection() {
  const { org } = useOutletContext<AdminOutletContext>();
  const [domains, setDomains] = useState<OrgDomainOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newDomain, setNewDomain] = useState("");
  const [adding, setAdding] = useState(false);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);

  const load = useCallback(() => {
    api<ListResponse<OrgDomainOut>>("GET", `/orgs/${org.id}/domains`)
      .then((res) => setDomains(res.items))
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load domains."));
  }, [org.id]);

  useEffect(() => {
    load();
  }, [load]);

  async function onAdd() {
    const domain = newDomain.trim();
    if (!domain) return;
    setAdding(true);
    setError(null);
    try {
      await api("POST", `/orgs/${org.id}/domains`, { domain });
      setNewDomain("");
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't add that domain.");
    } finally {
      setAdding(false);
    }
  }

  async function onVerify(id: string) {
    setVerifyingId(id);
    setError(null);
    try {
      await api("POST", `/orgs/${org.id}/domains/${id}/verify`);
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Not verified yet.");
    } finally {
      setVerifyingId(null);
    }
  }

  async function onDelete(id: string) {
    setError(null);
    try {
      await api("DELETE", `/orgs/${org.id}/domains/${id}`);
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't remove that domain.");
    }
  }

  return (
    <SettingsSection title="Domain verification and auto-join" description="New signups from a verified domain join automatically as members.">
      <div className="admin-inline-form">
        <Field label="Domain" className="org-settings__domain" placeholder="acme.com" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} />
        {/* Secondary: Create link is the one purple action on the Joining tab. */}
        <Button variant="secondary" onClick={onAdd} loading={adding} disabled={!newDomain.trim()}>
          Add
        </Button>
      </div>
      {error ? <Notice tone="error">{error}</Notice> : null}
      {!domains ? null : (
        <div className="list">
          {domains.map((d) => (
            <div className="list-row" key={d.id}>
              <div className="list-row__body">
                <div className="list-row__title">
                  {d.domain}
                  <Badge size="sm" tone={d.verified_at ? "success" : "neutral"}>
                    {d.verified_at ? "Verified" : "Pending"}
                  </Badge>
                </div>
                {!d.verified_at ? (
                  <div className="list-row__sub mono">
                    Add a TXT record at _grackle-verify.{d.domain} containing: {d.verification_token}
                  </div>
                ) : (
                  <div className="list-row__sub">New signups from this domain join automatically as members.</div>
                )}
              </div>
              {!d.verified_at ? (
                <Button variant="secondary" size="sm" onClick={() => onVerify(d.id)} disabled={verifyingId === d.id}>
                  {verifyingId === d.id ? "Checking…" : "Verify"}
                </Button>
              ) : null}
              <IconButton icon="trash" label={`Remove ${d.domain}`} size="sm" variant="muted" onClick={() => onDelete(d.id)} />
            </div>
          ))}
          {domains.length === 0 ? <div className="admin-row-sub">No domains added yet.</div> : null}
        </div>
      )}
    </SettingsSection>
  );
}

// Join links: a single reusable link instead of one invite per email.
function JoinLinksSection() {
  const { org } = useOutletContext<AdminOutletContext>();
  const [links, setLinks] = useState<JoinLinkOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState<JoinableRole>("member");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<JoinLinkCreated | null>(null);

  const load = useCallback(() => {
    api<ListResponse<JoinLinkOut>>("GET", `/orgs/${org.id}/join-links`)
      .then((res) => setLinks(res.items))
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load join links."));
  }, [org.id]);

  useEffect(() => {
    load();
  }, [load]);

  async function onCreate() {
    setCreating(true);
    setError(null);
    try {
      const res = await api<JoinLinkCreated>("POST", `/orgs/${org.id}/join-links`, { role });
      setCreated(res);
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't create a join link.");
    } finally {
      setCreating(false);
    }
  }

  async function onRevoke(id: string) {
    setError(null);
    try {
      await api("DELETE", `/orgs/${org.id}/join-links/${id}`);
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't revoke that link.");
    }
  }

  return (
    <SettingsSection title="Join links" description="One reusable link instead of an invite per email. The link is shown once, when it is created.">
      <div className="admin-inline-form">
        <Select
          label="They join as"
          className="admin-field--narrow"
          value={role}
          onChange={(v) => setRole(v as JoinableRole)}
          options={[
            { value: "member", label: "Member" },
            { value: "admin", label: "Admin" },
            { value: "guest", label: "Guest" },
          ]}
        />
        <Button onClick={onCreate} loading={creating}>
          Create link
        </Button>
      </div>
      {created ? (
        <div className="list-row">
          <div className="list-row__body">
            <div className="list-row__sub mono" style={{ wordBreak: "break-all" }}>{created.url}</div>
          </div>
        </div>
      ) : null}
      {error ? <Notice tone="error">{error}</Notice> : null}
      {!links ? null : (
        <div className="list">
          {links.filter((l) => !l.revoked).map((l) => (
            <div className="list-row" key={l.id}>
              <div className="list-row__body">
                <div className="list-row__title">
                  Joins as {l.role}
                  <Badge size="sm">{l.use_count} used</Badge>
                </div>
                <div className="list-row__sub">Created {new Date(l.created_at).toLocaleDateString()}</div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => onRevoke(l.id)}>
                Revoke
              </Button>
            </div>
          ))}
          {links.filter((l) => !l.revoked).length === 0 ? <div className="admin-row-sub">No active join links.</div> : null}
        </div>
      )}
    </SettingsSection>
  );
}

// FR-API2 / FR-API6: admin-minted organization tokens for the hosted MCP endpoint.
export function OrgTokensSection() {
  const { org } = useOutletContext<AdminOutletContext>();
  const [tokens, setTokens] = useState<AccessTokenOut[] | null>(null);
  const [name, setName] = useState("");
  const [access, setAccess] = useState<TokenAccess>("write");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<AccessTokenCreated | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api<ListResponse<AccessTokenOut>>("GET", `/orgs/${org.id}/tokens`)
      .then((res) => setTokens(res.items))
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load tokens."));
  }, [org.id]);

  useEffect(() => {
    load();
  }, [load]);

  async function onCreate() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    setError(null);
    try {
      const res = await api<AccessTokenCreated>("POST", `/orgs/${org.id}/tokens`, {
        name: trimmed,
        scopes: accessToScopes(access),
      });
      setCreated(res);
      setName("");
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't create that token.");
    } finally {
      setCreating(false);
    }
  }

  async function onRevoke(id: string) {
    setError(null);
    try {
      await api("DELETE", `/orgs/${org.id}/tokens/${id}`);
      if (created?.id === id) setCreated(null);
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't revoke that token.");
    }
  }

  return (
    <SettingsSection
      title="Access tokens"
      description={
        <>
          Organization tokens for Grok Bot or Cursor. Scoped to {org.name}. Connect at <span className="mono">/mcp</span> with the Bearer header. The secret is shown once.
        </>
      }
    >
      <div className="admin-inline-form">
        <Field label="Token name" placeholder="CI bot" value={name} onChange={(e) => setName(e.target.value)} />
        <TokenAccessPicker value={access} onChange={setAccess} />
        <Button onClick={onCreate} loading={creating} disabled={!name.trim()}>
          Create token
        </Button>
      </div>
      {created ? (
        <div className="list-row">
          <div className="list-row__body">
            <div className="list-row__sub">Copy this now — it will not be shown again.</div>
            <div className="list-row__sub mono" style={{ wordBreak: "break-all" }}>{created.secret}</div>
          </div>
        </div>
      ) : null}
      {error ? <Notice tone="error">{error}</Notice> : null}
      {!tokens ? null : (
        <div className="list">
          {tokens.map((t) => (
            <div className="list-row" key={t.id}>
              <div className="list-row__body">
                <div className="list-row__title">
                  {t.name}
                  <Badge size="sm">org</Badge>
                  <Badge size="sm">{scopesLabel(t.scopes)}</Badge>
                </div>
                <div className="list-row__sub mono">
                  {t.token_prefix}… · created {new Date(t.created_at).toLocaleDateString()}
                  {t.last_used_at ? ` · last used ${new Date(t.last_used_at).toLocaleString()}` : " · never used"}
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => onRevoke(t.id)}>
                Revoke
              </Button>
            </div>
          ))}
          {tokens.length === 0 ? <div className="admin-row-sub">No organization tokens yet.</div> : null}
        </div>
      )}
    </SettingsSection>
  );
}

export function exportStatusLabel(status: OrgExportOut["status"]): string {
  return status === "done" ? "Ready" : status === "failed" ? "Failed" : status === "running" ? "Building…" : "Queued";
}

/** Exported for `OrgSettingsPage.test.ts` (W7: "export section shows progress then download") —
 * which affordance `ExportSection` shows for a given export status. */
export function exportPhase(status: OrgExportOut["status"]): "progress" | "download" | "failed" {
  if (status === "done") return "download";
  if (status === "failed") return "failed";
  return "progress";
}

// Full org data export (FR-AD6): a background job builds metadata.json (+ media, under a size
// cap) into a tar archive; this section requests one, polls its status, and links the download
// once it's done. `GET .../download` is a plain navigation (it 302s to a freshly-signed URL),
// not a fetch call — the browser's cookies ride along the same as any other same-origin link.
function ExportSection() {
  const { org } = useOutletContext<AdminOutletContext>();
  const [exports, setExports] = useState<OrgExportOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);

  const load = useCallback(() => {
    api<ListResponse<OrgExportOut>>("GET", `/orgs/${org.id}/exports`)
      // F13b: this endpoint also returns every member's own "download my data" (`scope:
      // "user"`) export to an admin, for operator visibility — this section is specifically
      // the org-wide export, so only its rows belong here (the per-user ones show on each
      // member's own Account page instead).
      .then((res) => setExports(res.items.filter((e) => e.scope === "org")))
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load exports."));
  }, [org.id]);

  useEffect(() => {
    load();
  }, [load]);

  // A queued/running export is polled every few seconds so "Building…" resolves to a download
  // link without the admin needing to refresh the page themselves.
  useEffect(() => {
    if (!exports?.some((e) => e.status === "queued" || e.status === "running")) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [exports, load]);

  async function onRequest() {
    setRequesting(true);
    setError(null);
    try {
      await api("POST", `/orgs/${org.id}/exports`, { scope: "org" });
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't start an export.");
    } finally {
      setRequesting(false);
    }
  }

  return (
    <SettingsSection
      title="Data export"
      description={`A downloadable archive of ${org.name}'s members, teams, and recording metadata (plus each recording's original upload, when the org's total size allows it).`}
    >
      {error ? <Notice tone="error">{error}</Notice> : null}
      <div>
        <Button variant="secondary" onClick={onRequest} loading={requesting}>
          Request export
        </Button>
      </div>
      {!exports ? null : exports.length === 0 ? (
        <div className="admin-row-sub">No exports yet.</div>
      ) : (
        <div className="list">
          {exports.map((e) => (
            <div className="list-row" key={e.id}>
              <div className="list-row__body">
                <div className="list-row__title">
                  {exportStatusLabel(e.status)}
                  {e.status === "done" ? (
                    <Badge size="sm">{e.media_included ? "with media" : "metadata only"}</Badge>
                  ) : null}
                </div>
                {exportPhase(e.status) === "progress" ? (
                  <div className="list-row__sub" role="status" aria-live="polite">
                    <Icon name="circle-notch" size={14} className="spin" />{" "}
                    {e.status === "queued" ? "Waiting for a worker…" : "Building the archive…"}
                  </div>
                ) : null}
                <div className="list-row__sub">
                  Requested {new Date(e.created_at).toLocaleString()}
                  {e.status === "done" && e.expires_at
                    ? ` · link expires ${new Date(e.expires_at).toLocaleString()}`
                    : ""}
                  {e.status === "failed" && e.error ? ` — ${e.error}` : ""}
                </div>
              </div>
              {exportPhase(e.status) === "download" ? (
                <a className="gk-btn gk-btn--secondary gk-btn--sm" href={`/api/v1/orgs/${org.id}/exports/${e.id}/download`}>
                  Download
                </a>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </SettingsSection>
  );
}

// Org deletion: request → (grace period) → confirm, or cancel any time before confirming.
// Owner only (stricter than the admin bar the rest of this page uses).
function DeleteOrgSection() {
  const { org } = useOutletContext<AdminOutletContext>();
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [requestedAt, setRequestedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (org.role !== "owner") return;
    api<OrgPolicyOut>("GET", `/orgs/${org.id}/policy`)
      .then((res) => setRequestedAt(res.delete_requested_at))
      .catch(() => {});
  }, [org.id, org.role]);

  if (org.role !== "owner") return null;

  async function onRequest() {
    if (!window.confirm(`Start deleting ${org.name}? This can be canceled any time before it's finalized.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api("POST", `/orgs/${org.id}/request-deletion`);
      setRequestedAt(new Date().toISOString());
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't start deletion.");
    } finally {
      setBusy(false);
    }
  }

  async function onCancel() {
    setBusy(true);
    setError(null);
    try {
      await api("POST", `/orgs/${org.id}/cancel-deletion`);
      setRequestedAt(null);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't cancel deletion.");
    } finally {
      setBusy(false);
    }
  }

  async function onConfirm(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("POST", `/orgs/${org.id}/confirm-deletion`);
      await refresh();
      navigate("/orgs", { replace: true });
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Deletion can't be finalized yet.");
      setBusy(false);
    }
  }

  // Starting deletion is reversible through the grace period, so it's a quiet secondary button
  // beside the copy; only finalizing is the danger action.
  return (
    <SettingsSection
      title="Delete organization"
      className="admin-danger"
      description={`Permanently deletes ${org.name} for every member. Deletion has a grace period before it can be finalized.`}
      action={
        requestedAt ? null : (
          <Button variant="secondary" icon="trash" onClick={onRequest} loading={busy}>
            Delete organization
          </Button>
        )
      }
    >
      {error ? <Notice tone="error">{error}</Notice> : null}
      {requestedAt ? (
        <div className="admin-actions">
          <Button variant="danger" onClick={onConfirm} loading={busy}>
            Finalize deletion
          </Button>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel deletion
          </Button>
        </div>
      ) : null}
    </SettingsSection>
  );
}

export type SettingsTab = "general" | "sharing" | "meetings" | "joining" | "tokens";

export const SETTINGS_TABS: { value: SettingsTab; label: string }[] = [
  { value: "general", label: "General" },
  { value: "sharing", label: "Sharing" },
  { value: "meetings", label: "Meetings" },
  { value: "joining", label: "Joining" },
  { value: "tokens", label: "Access tokens" },
];

export function isSettingsTab(value: string | null): value is SettingsTab {
  return SETTINGS_TABS.some((t) => t.value === value);
}

export default function OrgSettingsPage() {
  const { org } = useOutletContext<AdminOutletContext>();
  const { refresh } = useSession();
  const pageRef = useRef<HTMLDivElement>(null);
  // The tab is in the URL (like AI and models) so a tab is linkable and survives a reload.
  const [params, setParams] = useSearchParams();
  const rawTab = params.get("tab");
  const tab: SettingsTab = isSettingsTab(rawTab) ? rawTab : "general";

  function goTo(next: SettingsTab) {
    const nextParams = new URLSearchParams(params);
    if (next === "general") nextParams.delete("tab");
    else nextParams.set("tab", next);
    setParams(nextParams, { replace: true });
    // The shell's content column scrolls (desktop or MobileShell), not the window.
    pageRef.current?.closest(".app-shell__main, .mshell__content")?.scrollTo?.({ top: 0 });
  }

  const [usage, setUsage] = useState<OrgUsageOut | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);

  const [name, setName] = useState(org.name);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const loadUsage = useCallback(() => {
    api<OrgUsageOut>("GET", `/orgs/${org.id}/usage`)
      .then(setUsage)
      .catch((err) => setUsageError(err instanceof ApiFail ? err.message : "Couldn't load usage."));
  }, [org.id]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  useEffect(() => {
    setName(org.name);
  }, [org.name]);

  async function onSave() {
    const trimmed = name.trim();
    if (!trimmed || trimmed === org.name) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await api<OrgOut>("PATCH", `/orgs/${org.id}`, { name: trimmed });
      await refresh();
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof ApiFail ? err.message : "Couldn't save that name.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="org-settings" ref={pageRef}>
      <div className="org-settings__head">
        <header className="page-head">
          <div>
            <h1>Settings</h1>
            <p>Organization identity and usage.</p>
          </div>
        </header>
        <Tabs items={SETTINGS_TABS} value={tab} onChange={(v) => goTo(v as SettingsTab)} />
      </div>

      <div className="org-settings__panel" role="tabpanel" aria-label={SETTINGS_TABS.find((t) => t.value === tab)?.label}>
        {tab === "general" ? (
          <>
            {usageError ? <Notice tone="error">{usageError}</Notice> : null}
            {usage ? (
              <div className="admin-stats org-settings__stats">
                <Stat label="Members" value={usage.member_count} />
                <Stat label="Teams" value={usage.team_count} />
                <Stat label="Recordings" value={usage.grackle_count} />
                <Stat label="Storage" value={formatBytes(usage.storage_bytes)} caption="Approximate" />
              </div>
            ) : usageError ? null : (
              <div className="admin-row-sub">Loading…</div>
            )}

            <SettingsSection title="Organization" description="Everyone you invite lands here by default.">
              <div className="admin-two-col">
                <Field
                  label="Organization name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setSaved(false);
                  }}
                />
                <Field label="Organization URL" value={`grackle.app/${org.slug}`} readOnly help="Permanent, set at creation" />
              </div>
              {saveError ? <Notice tone="error">{saveError}</Notice> : null}
              <SaveRow
                onSave={onSave}
                onCancel={name !== org.name ? () => { setName(org.name); setSaved(false); } : undefined}
                saving={saving}
                disabled={!name.trim() || name.trim() === org.name}
                status={saved ? "Saved." : undefined}
              />
            </SettingsSection>

            <ExportSection />
            <DeleteOrgSection />
          </>
        ) : tab === "sharing" ? (
          <PolicySection />
        ) : tab === "meetings" ? (
          <MeetingPolicySection />
        ) : tab === "joining" ? (
          <>
            <DomainsSection />
            <JoinLinksSection />
          </>
        ) : (
          <OrgTokensSection />
        )}
      </div>
    </div>
  );
}
